import { ExpenseBurden, ExpenseHistory } from '@tosho/domain';
import type { FinancialOverview } from './financial-overview-api';
import { remunerationMoney } from './RemunerationColumn';
import annualIcon from '../assets/finance/remuneration-annual.svg?no-inline';
import { FinancialCalculationStatus } from './FinancialCalculationStatus';

type Year = NonNullable<FinancialOverview['expenseHistory']>['years'][number];
const burden = new ExpenseBurden();
const ratioLabels = ['Plus de 100 %', '> 65 à 100 %', '> 25 à 50 %', '> 50 à 65 %', 'Jusqu’à 25 %'];
const periodName = (period: string): string =>
  new Intl.DateTimeFormat('fr-FR', { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${period}-01T12:00:00Z`),
  );

/** Le serveur fixe le mois de situation. Le repli accompagne le déploiement
 * progressif et le jeu de démonstration : l'historique finit au mois de situation,
 * jamais au mois choisi dans le tableau voisin ni à l'horloge du navigateur. */
function annualRows(overview: FinancialOverview): readonly Year[] {
  if (overview.expenseHistory) return overview.expenseHistory.years;
  const months = overview.remuneration?.months ?? [];
  const asOfMonth = months
    .map((month) => month.period)
    .sort()
    .at(-1);
  if (!asOfMonth) return [];
  const history = new ExpenseHistory(
    months.map((month) => ({
      period: month.period,
      expensesComplete: month.expensesComplete === true && month.expenses !== undefined,
      clientReceiptsCents:
        month.clientReceiptsCents == null ? null : BigInt(month.clientReceiptsCents),
      expenses: (month.expenses ?? []).map((expense) => ({
        ...expense,
        paidCents: BigInt(expense.paidCents),
        htCents: expense.htCents === null ? null : BigInt(expense.htCents),
      })),
    })),
  );
  return Array.from({ length: Number(asOfMonth.slice(0, 4)) - 2019 }, (_, index) => {
    const row = history.annual(2020 + index, asOfMonth);
    return {
      ...row,
      averagePaidCents: row.averagePaidCents?.toString() ?? null,
      totalPaidCents: row.totalPaidCents?.toString() ?? null,
      excludedDeboursCents: row.excludedDeboursCents?.toString() ?? null,
      receiptsCents: row.receiptsCents?.toString() ?? null,
    };
  });
}

export function AnnualExpensesCard({
  overview,
}: {
  readonly overview: FinancialOverview;
}): React.JSX.Element {
  const years = annualRows(overview);
  const maximum = Math.max(
    1,
    ...years.map((year) => Math.max(0, Number(year.averagePaidCents ?? 0))),
  );
  return (
    <article className="expense-annual-card" aria-label="Dépenses par année">
      <h3>
        <img src={annualIcon} alt="" />
        Mes dépenses par année
      </h3>
      <div
        className="expense-annual-card__chart"
        role="group"
        aria-label="Dépenses moyennes par mois clos"
      >
        <div className="expense-annual-card__columns" aria-hidden="true">
          <span>Année</span>
          <span>Moyenne payée / mois</span>
        </div>
        {years.map((year) => {
          const level =
            year.ratioBasisPoints === null
              ? null
              : burden.level(
                  year.totalPaidCents === null ? null : BigInt(year.totalPaidCents),
                  year.receiptsCents === null ? null : BigInt(year.receiptsCents),
                );
          return (
            <details
              className={`expense-year expense-burden-level-${level ?? 'unknown'}`}
              key={year.year}
            >
              <summary>
                <span className="expense-year__year">{year.year}</span>
                <span className="expense-year__track" aria-hidden="true">
                  {year.averagePaidCents !== null && BigInt(year.averagePaidCents) > 0n ? (
                    <i style={{ width: `${(Number(year.averagePaidCents) / maximum) * 100}%` }} />
                  ) : null}
                </span>
                <span className="expense-year__amount">
                  {year.averagePaidCents === null ? (
                    <FinancialCalculationStatus calculation="expenseHistory" label="Indisponible" />
                  ) : (
                    remunerationMoney(year.averagePaidCents)
                  )}
                </span>
                <span className="expense-year__coverage">
                  {year.ratioBasisPoints !== null ? (
                    <span className="expense-year__ratio">
                      {new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(
                        year.ratioBasisPoints / 100,
                      )}{' '}
                      % des recettes ·{' '}
                    </span>
                  ) : null}
                  {year.expectedMonths === 0
                    ? 'Aucun mois clos'
                    : `${year.knownMonths}/${year.expectedMonths} mois clos disponibles`}
                  {year.knownMonths > 0 && year.knownMonths < year.expectedMonths
                    ? ' · moyenne partielle'
                    : ''}
                  {year.averagePaidCents !== null && BigInt(year.averagePaidCents) < 0n
                    ? ' · remboursements nets'
                    : ''}
                </span>
              </summary>
              <dl>
                <div>
                  <dt>Période examinée</dt>
                  <dd>
                    {year.throughPeriod === null
                      ? 'Aucun mois clos'
                      : `${periodName(year.fromPeriod)} – ${periodName(year.throughPeriod)}`}
                  </dd>
                </div>
                <div>
                  <dt>Total des sorties nettes connues</dt>
                  <dd>{remunerationMoney(year.totalPaidCents)}</dd>
                </div>
                <div>
                  <dt>Calcul de la moyenne</dt>
                  <dd>
                    {year.knownMonths ? (
                      `${remunerationMoney(year.totalPaidCents)} ÷ ${year.knownMonths} mois = ${remunerationMoney(year.averagePaidCents)}/mois`
                    ) : (
                      <FinancialCalculationStatus
                        calculation="expenseHistory"
                        label="Données insuffisantes"
                      />
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Recettes clients TTC sur ces mêmes mois</dt>
                  <dd>{remunerationMoney(year.receiptsCents)}</dd>
                </div>
                <div>
                  <dt>Sorties pour 100 € encaissés</dt>
                  <dd>
                    {year.ratioBasisPoints === null ? (
                      <FinancialCalculationStatus calculation="expenseHistory" />
                    ) : (
                      `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(year.ratioBasisPoints / 100)} €`
                    )}
                  </dd>
                </div>
                {year.excludedDeboursCents !== null && year.excludedDeboursCents !== '0' ? (
                  <div>
                    <dt>Débours écartés du calcul</dt>
                    <dd>{remunerationMoney(year.excludedDeboursCents)}</dd>
                  </div>
                ) : null}
              </dl>
              {year.knownMonths < year.expectedMonths ? (
                <p>
                  Les mois absents ou incomplets ne comptent pas comme zéro. Cette moyenne n’est pas
                  celle de toute l’année.
                </p>
              ) : null}
              {year.ratioBasisPoints === null && year.knownMonths > 0 ? (
                <p>
                  Le ratio exige des encaissements connus et positifs sur tous les mois utilisés.
                </p>
              ) : null}
            </details>
          );
        })}
        {!years.length ? <p>Historique des dépenses indisponible.</p> : null}
      </div>
      <ul
        className="expense-annual-card__legend"
        aria-label="Part des recettes clients consacrée aux dépenses"
      >
        {[4, 2, 3, 1, 0].map((level) => (
          <li className={`expense-burden-level-${level}`} key={level}>
            <i />
            {ratioLabels[level]}
          </li>
        ))}
      </ul>
      <details className="expense-annual-card__method">
        <summary>Qu’est-ce qui est compté ?</summary>
        <p>
          Barres : moyenne des sorties payées sur les mois clos connus, remboursements déduits, hors
          rémunération et débours. Le mois en cours est exclu.
        </p>
        <p>
          Couleurs : part des recettes clients TTC consacrée à ces sorties sur les mêmes mois, selon
          les repères existants de 25, 50, 65 et 100 %. Ce n’est pas une marge. Gris : ratio
          indisponible. Les mois inconnus ne comptent jamais comme zéro.
        </p>
      </details>
    </article>
  );
}
